import type {
  FindingsGroupByField,
  FindingsSortBy,
  FindingsThenSortBy,
} from '@terra/shared-contracts-findings';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type {
  FindingSeverityType,
  FindingStatusType,
  SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';
import type { FindingsBulkFilter } from '../../../clients/testerClient';
import type {
  FindingHasPreventionFilter,
  FindingPublishedFilter,
} from '../../../store/tab/findingsTabScopedFilterStore';
import { resolveFindingsPublishedApiFilter } from '../../../utils/findingsPublishedFilter';
import {
  FINDINGS_GROUP_SORT,
  FINDINGS_GROUPINGS_WITHOUT_THEN_SORT,
  type FindingsGroupSort,
} from '@terra/ui-design-system';

export const FINDINGS_COLUMN_TO_SORT_FIELD = {
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  status: 'status',
  isPublished: 'isPublished',
  severity: 'severity',
  title: 'title',
  category: 'category',
  assets: 'assets',
  relatedSignal: 'signalId',
} as const satisfies Record<string, FindingsSortBy>;

export type FindingsSortableColumn = keyof typeof FINDINGS_COLUMN_TO_SORT_FIELD;

export type FindingsColumnSort = {
  colId: FindingsSortableColumn;
  sort: 'asc' | 'desc';
};

export const DEFAULT_FINDINGS_COLUMN_SORT: FindingsColumnSort = {
  colId: 'createdAt',
  sort: 'desc',
};

export const isFindingsSortableColumn = (
  value: string | undefined,
): value is FindingsSortableColumn =>
  value !== undefined &&
  Object.prototype.hasOwnProperty.call(FINDINGS_COLUMN_TO_SORT_FIELD, value);

const FINDINGS_COLUMN_TO_THEN_SORT_FIELD = {
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  isPublished: 'isPublished',
  title: 'title',
  category: 'category',
  relatedSignal: 'signalId',
} as const satisfies Partial<
  Record<FindingsSortableColumn, FindingsThenSortBy>
>;

type FindingsGroupedSortableColumn =
  keyof typeof FINDINGS_COLUMN_TO_THEN_SORT_FIELD;

export type FindingsListSort = FindingsGroupSort & {
  thenSortBy?: FindingsThenSortBy;
  thenSortOrder?: 'asc' | 'desc';
};

const isFindingsGroupedSortableColumnId = (
  colId: string,
): colId is FindingsGroupedSortableColumn =>
  Object.prototype.hasOwnProperty.call(
    FINDINGS_COLUMN_TO_THEN_SORT_FIELD,
    colId,
  );

const resolveFindingsThenSortField = ({
  colId,
  groupByField,
}: {
  colId: string | undefined;
  groupByField: FindingsGroupByField;
}): FindingsThenSortBy | null =>
  colId !== undefined &&
  colId !== groupByField &&
  !FINDINGS_GROUPINGS_WITHOUT_THEN_SORT.has(groupByField) &&
  isFindingsGroupedSortableColumnId(colId)
    ? FINDINGS_COLUMN_TO_THEN_SORT_FIELD[colId]
    : null;

export type FindingsGroupSortOrder = 'asc' | 'desc' | null;

export const FINDINGS_GROUP_COLUMN_ID: Partial<
  Record<FindingsGroupByField, FindingsSortableColumn>
> = {
  severity: 'severity',
  status: 'status',
  category: 'category',
  relatedEndpoint: 'assets',
  createdAt: 'createdAt',
  relatedSignal: 'relatedSignal',
  title: 'title',
  isPublished: 'isPublished',
};

const resolveFindingsGroupSort = ({
  groupByField,
  groupSortOrder,
}: {
  groupByField: FindingsGroupByField;
  groupSortOrder: FindingsGroupSortOrder;
}): FindingsGroupSort => {
  const groupSort = FINDINGS_GROUP_SORT[groupByField];

  return groupSortOrder
    ? { ...groupSort, sortOrder: groupSortOrder }
    : groupSort;
};

export const isFindingsColumnSortableInView = ({
  colId,
  groupByField,
}: {
  colId: string | undefined;
  groupByField: FindingsGroupByField | null;
}): boolean =>
  groupByField === null
    ? isFindingsSortableColumn(colId)
    : (colId !== undefined &&
        colId === FINDINGS_GROUP_COLUMN_ID[groupByField]) ||
      resolveFindingsThenSortField({ colId, groupByField }) !== null;

export const resolveFindingsListSort = ({
  columnSort,
  groupByField,
  groupSortOrder,
}: {
  columnSort: FindingsColumnSort;
  groupByField: FindingsGroupByField | null;
  groupSortOrder: FindingsGroupSortOrder;
}): FindingsListSort => {
  if (groupByField === null) {
    return {
      sortBy: FINDINGS_COLUMN_TO_SORT_FIELD[columnSort.colId],
      sortOrder: columnSort.sort,
    };
  }

  const groupSort = resolveFindingsGroupSort({ groupByField, groupSortOrder });
  const thenSortBy = resolveFindingsThenSortField({
    colId: columnSort.colId,
    groupByField,
  });

  return thenSortBy
    ? { ...groupSort, thenSortBy, thenSortOrder: columnSort.sort }
    : groupSort;
};

export type FindingsSortState = {
  columnSort: FindingsColumnSort;
  groupByField: FindingsGroupByField | null;
  groupSortOrder: FindingsGroupSortOrder;
};

export const resolveFindingsColumnSortState = ({
  columnSort,
  groupByField,
  groupSortOrder,
}: FindingsSortState): Array<{
  colId: string;
  sort: 'asc' | 'desc';
  sortIndex: number;
}> => {
  if (groupByField === null) {
    return isFindingsSortableColumn(columnSort.colId)
      ? [{ colId: columnSort.colId, sort: columnSort.sort, sortIndex: 0 }]
      : [];
  }

  const groupColumnId = FINDINGS_GROUP_COLUMN_ID[groupByField];
  const thenSortBy = resolveFindingsThenSortField({
    colId: columnSort.colId,
    groupByField,
  });
  const thenSortColumn =
    thenSortBy && columnSort.colId !== groupColumnId
      ? { colId: columnSort.colId, sort: columnSort.sort }
      : null;

  if (!groupColumnId) {
    return thenSortColumn ? [{ ...thenSortColumn, sortIndex: 0 }] : [];
  }

  const groupColumn = {
    colId: groupColumnId,
    sort: resolveFindingsGroupSort({ groupByField, groupSortOrder }).sortOrder,
    sortIndex: 0,
  };

  return thenSortColumn
    ? [groupColumn, { ...thenSortColumn, sortIndex: 1 }]
    : [groupColumn];
};

export const resolveFindingsSortChange = ({
  sortedColumn,
  columnSort,
  groupByField,
  groupSortOrder,
}: FindingsSortState & {
  sortedColumn: { colId: string; sort: 'asc' | 'desc' } | null;
}): Pick<FindingsSortState, 'columnSort' | 'groupSortOrder'> => {
  const groupColumnId =
    groupByField === null ? undefined : FINDINGS_GROUP_COLUMN_ID[groupByField];

  if (sortedColumn && groupColumnId && sortedColumn.colId === groupColumnId) {
    return { columnSort, groupSortOrder: sortedColumn.sort };
  }

  if (
    !sortedColumn ||
    !isFindingsSortableColumn(sortedColumn.colId) ||
    !isFindingsColumnSortableInView({ colId: sortedColumn.colId, groupByField })
  ) {
    return { columnSort: DEFAULT_FINDINGS_COLUMN_SORT, groupSortOrder };
  }

  return {
    columnSort: { colId: sortedColumn.colId, sort: sortedColumn.sort },
    groupSortOrder,
  };
};

export const resolveFindingsColumnSort = (
  sortModel: ReadonlyArray<{ colId: string; sort: 'asc' | 'desc' }>,
): FindingsColumnSort => {
  const persisted = sortModel.find((entry) =>
    isFindingsSortableColumn(entry.colId),
  );

  return persisted && isFindingsSortableColumn(persisted.colId)
    ? { colId: persisted.colId, sort: persisted.sort }
    : DEFAULT_FINDINGS_COLUMN_SORT;
};

export const FINDINGS_PUBLICATION_CHIP = {
  ALL: 'all',
  PUBLISHED: 'published',
  UNPUBLISHED: 'unpublished',
} as const;

export type FindingsPublicationChip =
  (typeof FINDINGS_PUBLICATION_CHIP)[keyof typeof FINDINGS_PUBLICATION_CHIP];

export const resolveFindingsPublicationChip = (
  publishedFilter: FindingPublishedFilter,
): FindingsPublicationChip => {
  switch (publishedFilter) {
    case 'ALL':
      return FINDINGS_PUBLICATION_CHIP.ALL;
    case 'published':
    case 'auto-published':
      return FINDINGS_PUBLICATION_CHIP.PUBLISHED;
    case 'unpublished':
      return FINDINGS_PUBLICATION_CHIP.UNPUBLISHED;
  }
};

export const toFindingsPublishedFilter = (
  chip: FindingsPublicationChip,
): FindingPublishedFilter => {
  switch (chip) {
    case FINDINGS_PUBLICATION_CHIP.ALL:
      return 'ALL';
    case FINDINGS_PUBLICATION_CHIP.PUBLISHED:
      return 'published';
    case FINDINGS_PUBLICATION_CHIP.UNPUBLISHED:
      return 'unpublished';
  }
};

export type FindingsListFilterInput = {
  endpointId?: string;
  searchTerm: string;
  severityFilter: readonly FindingSeverityType[];
  statusFilters: readonly FindingStatusType[];
  categoryFilter: readonly SignalCategoryType[];
  publishedFilter: FindingPublishedFilter;
  hasPreventionFilter: FindingHasPreventionFilter;
  hasPendingSeverityChangeRequestFilter?: boolean;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  lifecycleState?: LifecycleStateFilterDto;
};

const toOptionalList = <TValue>(
  values: readonly TValue[],
): TValue[] | undefined => (values.length ? [...values] : undefined);

export const buildFindingsBulkFilter = (
  input: FindingsListFilterInput,
): FindingsBulkFilter => ({
  endpointId: input.endpointId,
  searchTerm: input.searchTerm.trim() || undefined,
  severity: toOptionalList(input.severityFilter),
  status: toOptionalList(input.statusFilters),
  category: toOptionalList(input.categoryFilter),
  ...resolveFindingsPublishedApiFilter(input.publishedFilter),
  hasPrevention:
    input.hasPreventionFilter === 'ALL'
      ? undefined
      : input.hasPreventionFilter === 'yes',
  hasPendingSeverityChangeRequest: input.hasPendingSeverityChangeRequestFilter
    ? true
    : undefined,
  createdStartDate: input.createdStartDate ?? undefined,
  createdEndDate: input.createdEndDate ?? undefined,
  lifecycleState: input.lifecycleState,
});

export const omitFindingsPublicationFilter = ({
  isPublished: _isPublished,
  wasAutoPublished: _wasAutoPublished,
  ...filter
}: FindingsBulkFilter): FindingsBulkFilter => filter;

export const hasActiveFindingsFilters = (
  input: Omit<FindingsListFilterInput, 'endpointId' | 'lifecycleState'>,
): boolean =>
  input.searchTerm.trim() !== '' ||
  input.severityFilter.length > 0 ||
  input.statusFilters.length > 0 ||
  input.categoryFilter.length > 0 ||
  input.publishedFilter === 'auto-published' ||
  input.hasPreventionFilter !== 'ALL' ||
  !!input.hasPendingSeverityChangeRequestFilter ||
  input.createdStartDate !== null ||
  input.createdEndDate !== null;
